import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { cpf } from "cpf-cnpj-validator";

import { useMutation, useQuery } from "@tanstack/react-query";
import { useFormik } from "formik";
import { IconX } from "@tabler/icons-react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid2,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import {
  formatCpf,
  formatPhone,
  maskCep,
  phoneValidate,
  unMask,
} from "../helpers/format";

import Loading from "./Loading";
import { Input } from "./Input";

import { checkCep } from "../services/app";
import { customerCreate } from "../services/customer";

const CustomerRegister = ({
  open,
  onClose,
  refetch,
  onAction,
}: {
  open: boolean;
  onClose: () => void;
  refetch: () => void;
  onAction: (e: any) => void;
}) => {
  const [value, setValue] = useState("1");

  const { mutate, isPending } = useMutation({
    mutationFn: customerCreate,
    onSuccess: (e: any) => {
      toast.success("Cliente cadastrado com sucesso");

      refetch();
      onAction(e?.id);
      resetForm();

      setTimeout(() => {
        onClose();
      }, 300);
    },
    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(String(e?.response?.data?.message) || "Erro!");
    },
  });

  const { values, handleSubmit, handleChange, setFieldValue, resetForm } =
    useFormik({
      initialValues: {
        name: "",
        nickName: "",
        document: "",
        email: "",
        phone: "",
        state: "",
        city: "",
        neighborhood: "",
        street: "",
        number: "",
        complement: "",
        cep: "",
        birthDate: "",
      },
      onSubmit: (values) => {
        const requiredFields = [
          { field: values.name, label: "Nome" },
          { field: values.document, label: "CPF" },
          { field: values.phone, label: "Telefone" },
          { field: values.email, label: "E-mail" },
          { field: values.cep, label: "CEP" },
          { field: values.street, label: "Rua" },
          { field: values.number, label: "Número" },
          { field: values.neighborhood, label: "Bairro" },
          { field: values.city, label: "Cidade" },
          { field: values.state, label: "Estado" },
        ];

        for (const item of requiredFields) {
          if (!item.field || item.field.trim() === "") {
            toast.error(`Preencha o campo "${item.label}"`);
            return;
          }
        }
        if (!cpf.isValid(unMask(values.document))) {
          toast.error("CPF inválido");
          return;
        }
        if (!phoneValidate(formatPhone(values.phone))) {
          toast.error("Telefone inválido");
          return;
        }

        mutate({
          ...values,
          phone: unMask(values.phone),
          cep: unMask(values.cep),
          document: unMask(values.document),
        });
      },
    });

  const handleChangeValue = (
    _event: React.SyntheticEvent,
    newValue: string
  ) => {
    setValue(newValue);
  };

  const {
    refetch: refetchCep,
    data,
    isFetching,
  } = useQuery({
    queryFn: async () => await checkCep(values.cep),
    queryKey: ["getCep", values.cep],
    enabled: false,
    retry: 1,
  });

  useEffect(() => {
    if (data) {
      setFieldValue("neighborhood", data?.neighborhood);
      setFieldValue("state", data?.state);
      setFieldValue("street", data?.address);
      setFieldValue("city", data?.city);
    }
  }, [data]);

  return (
    <Dialog
      maxWidth="sm"
      open={open}
      onClose={() => {
        onClose();
        resetForm();
      }}
      fullWidth
    >
      <Loading loading={isPending || isFetching} />
      <DialogContent>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 10, md: 11 }}>
            <Typography variant="h2" fontSize={26} fontWeight={600}>
              Cadastrar novo cliente
            </Typography>{" "}
            <Typography variant="subtitle1" color="secondary.contrastText">
              Preencha os dados do seu novo cliente.
            </Typography>
          </Grid2>

          <Grid2 size={{ xs: 2, md: 1 }}>
            <IconButton
              onClick={() => {
                onClose();
                resetForm();
              }}
            >
              <IconX style={styles.iconStyle} />
            </IconButton>
          </Grid2>
          <Grid2 size={12}>
            <Tabs
              sx={{
                borderColor: "divider",
              }}
              onChange={handleChangeValue}
              value={value}
              textColor="inherit"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="PREENCHA OS DADOS" value="1" />
            </Tabs>
          </Grid2>
          {value === "1" && (
            <>
              <Grid2 size={6}>
                <Input
                  id="name"
                  name="name"
                  label="Nome"
                  placeholder="Digite o nome completo"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.name}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={6}>
                <Input
                  id="document"
                  name="document"
                  label="CPF"
                  placeholder="Digite o CPF"
                  type="text"
                  fullWidth
                  size="small"
                  value={formatCpf(values.document)}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={6}>
                <Input
                  id="phone"
                  name="phone"
                  label="Telefone"
                  placeholder="Digite o telefone"
                  type="text"
                  fullWidth
                  size="small"
                  autoComplete="no-autofill-phone"
                  value={formatPhone(values.phone)}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={6}>
                <Input
                  id="email"
                  name="email"
                  label="E-mail"
                  placeholder="Digite o E-mail"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.email}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={12}>
                <Divider />
              </Grid2>
              <Grid2 size={12}>
                <Typography fontWeight={700}>Endereço</Typography>
              </Grid2>
              <Grid2 size={5}>
                <Input
                  id="cep"
                  name="cep"
                  label="CEP"
                  placeholder="Digite o CEP"
                  type="text"
                  fullWidth
                  size="small"
                  value={maskCep(values.cep)}
                  onChange={handleChange}
                  onBlur={(e) => {
                    if (e.target.value.length) {
                      refetchCep();
                    }
                  }}
                />
              </Grid2>
              <Grid2 size={7}>
                <Input
                  id="street"
                  name="street"
                  label="Rua"
                  placeholder="Digite a rua"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.street}
                  onChange={handleChange}
                />
              </Grid2>{" "}
              <Grid2 size={7}>
                <Input
                  id="neighborhood"
                  name="neighborhood"
                  label="Bairro"
                  placeholder="Digite o bairro"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.neighborhood}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={5}>
                <Input
                  id="number"
                  name="number"
                  label="Número"
                  placeholder="Digite a número"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.number}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={4}>
                <Input
                  id="city"
                  name="city"
                  label="Cidade"
                  placeholder="Digite a cidade"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.city}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={4}>
                <Input
                  id="state"
                  name="state"
                  label="Estado"
                  placeholder="Digite o estado"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.state}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={4}>
                <Input
                  id="complement"
                  name="complement"
                  label="Complemento (Opcional)"
                  placeholder="Digite o complemento"
                  type="text"
                  fullWidth
                  value={values.complement}
                  onChange={handleChange}
                />
              </Grid2>
            </>
          )}
        </Grid2>
      </DialogContent>

      <DialogActions>
        <Grid2 size={12}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => handleSubmit()}
          >
            Cadastrar cliente
          </Button>
        </Grid2>
      </DialogActions>
    </Dialog>
  );
};

const styles = {
  stackStyle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 1,
    padding: 1,
    borderRadius: 2,
    backgroundColor: "secondary.dark",
  },
  iconStyle: {
    color: "primary.main",
  },
};

export default CustomerRegister;
